import { Trans, useLingui } from '@lingui/react/macro'
import { billingHooks } from '$src/lib/trpcHooks/billing'
import {
  type PropertySubscription,
  type PropertyWithRelations,
} from '@markku/types'
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Stack,
  Text,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconSparkles, IconUserCheck } from '@tabler/icons-react'
import { useState } from 'react'
import { ContractTermsModal } from './ContractTermsModal'
import { SubscriptionPackageList } from './SubscriptionPackageList'

interface ProposalActivationCardProps {
  subscription: PropertySubscription
  property: Pick<PropertyWithRelations, 'id' | 'address'>
}

export function ProposalActivationCard({
  subscription,
  property,
}: ProposalActivationCardProps) {
  const { t } = useLingui()
  const { mutateAsync: acceptOffer, isPending: isAccepting } =
    billingHooks.useAcceptOfferCheckout()
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [termsOpened, { open: openTerms, close: closeTerms }] =
    useDisclosure(false)

  const priceMonthly = subscription.basePriceMonthly

  const handleActivate = async () => {
    await acceptOffer({ subscriptionId: subscription.id })
  }

  return (
    <Card withBorder radius="md" p="md" bg="green.0">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Group gap="xs">
              <Text fw={600} size="lg">
                <Trans>Markku recommends for you</Trans>
              </Text>
              <Badge
                color="green"
                variant="light"
                leftSection={<IconSparkles size={12} />}
              >
                <Trans>New</Trans>
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              {property.address?.street}, {property.address?.city}
            </Text>
          </Stack>
        </Group>

        <Group justify="space-between" align="center">
          <div>
            <Text fw={600}>{subscription.displayName}</Text>
            <Text size="xl" fw={700} c="green">
              <Trans>{priceMonthly} €/mo</Trans>
            </Text>
          </div>
        </Group>

        {subscription.subscriptionPackageVersions?.length && (
          <SubscriptionPackageList
            subscriptionPackageVersions={
              subscription.subscriptionPackageVersions
            }
          />
        )}

        {subscription.additionalTerms && (
          <Alert
            icon={<IconUserCheck size={16} />}
            color="green"
            variant="light"
          >
            <Text
              size="sm"
              fw={500}
              style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}
            >
              {subscription.additionalTerms}
            </Text>
          </Alert>
        )}

        <Checkbox
          checked={termsAccepted}
          onChange={(event) => setTermsAccepted(event.currentTarget.checked)}
          label={
            <Text size="sm">
              {t`I accept the`}{' '}
              <Anchor onClick={openTerms} size="sm">
                {t`terms and conditions`}
              </Anchor>
            </Text>
          }
        />

        <Button
          color="green"
          size="md"
          onClick={handleActivate}
          loading={isAccepting}
          disabled={!termsAccepted}
          fullWidth
        >
          <Trans>Activate package</Trans>
        </Button>
      </Stack>

      <ContractTermsModal opened={termsOpened} onClose={closeTerms} />
    </Card>
  )
}
